import { PauseCircle, PlayCircle, Trash2 } from 'lucide-react'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, EmptyState, PosButton } from '@/components/ui'
import { useHeldOrders } from '@/contexts/HeldOrdersContext'
import { useRegisterContext } from '@/contexts/RegisterContext'
import { calculateTotals } from '@/services'
import { formatMoney, formatTime } from '@/utils'

interface HeldOrdersDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Parked baskets (customer went back for something). Number keys 1–9 resume the matching row. */
export const HeldOrdersDialog = ({ open, onOpenChange }: HeldOrdersDialogProps) => {
  const { held, resume, discard } = useHeldOrders()
  const { tax_rate } = useRegisterContext()

  const onResume = (id: string) => {
    resume(id)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="md"
        onKeyDown={(e) => {
          const i = Number(e.key) - 1
          if (i >= 0 && held[i]) {
            e.preventDefault()
            onResume(held[i].id)
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Held orders</DialogTitle>
          <DialogDescription>Resuming swaps the held order into the cart. Anything in the cart now is held in its place.</DialogDescription>
        </DialogHeader>
        {held.length === 0 ? (
          <EmptyState icon={PauseCircle} title="No held orders">
            Press F6 during a sale to park it here.
          </EmptyState>
        ) : (
          <ul className="divide-y divide-border-subtle rounded-md border border-border">
            {held.map((h, i) => {
              const totals = calculateTotals(h.lines, h.discount, tax_rate)
              return (
                <li key={h.id} className="flex items-center gap-3 p-3">
                  <span className="grid size-7 shrink-0 place-items-center rounded-full bg-fill text-xs font-semibold text-ink">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{h.label}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {formatTime(h.held_at)} · {totals.itemCount} items · {h.lines.map((l) => l.product.name).join(', ')}
                    </p>
                  </div>
                  <span className="text-sm font-semibold tabular-nums text-ink">{formatMoney(totals.total)}</span>
                  <PosButton size="icon" variant="ghost" aria-label={`Discard ${h.label}`} onClick={() => discard(h.id)}>
                    <Trash2 />
                  </PosButton>
                  <PosButton onClick={() => onResume(h.id)}>
                    <PlayCircle /> Resume
                  </PosButton>
                </li>
              )
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  )
}
